import { useReducer, useMemo, useCallback } from 'react';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { useDebouncedValue } from '@/lib/utils';

// Static filter data - moved outside component to prevent recreation
const STATIC_FILTER_OPTIONS = {
  styleOptions: [
    { id: 'style-realistic', label: 'Realistic', checked: false },
    { id: 'style-cinematic', label: 'Cinematic', checked: false },
    { id: 'style-animated', label: 'Animated', checked: false },
    { id: 'style-artistic', label: 'Artistic', checked: false }
  ] as FilterOption[],
  
  influenceOptions: [
    { id: 'influence-nature', label: 'Nature', checked: false },
    { id: 'influence-urban', label: 'Urban', checked: false },
    { id: 'influence-fantasy', label: 'Fantasy', checked: false },
    { id: 'influence-scifi', label: 'Sci-Fi', checked: false }
  ] as FilterOption[],
  
  colorOptions: [
    { id: 'color-warm', label: 'Warm', checked: false },
    { id: 'color-cool', label: 'Cool', checked: false },
    { id: 'color-vibrant', label: 'Vibrant', checked: false },
    { id: 'color-muted', label: 'Muted', checked: false }
  ] as FilterOption[],
  
  backgroundOptions: [
    { id: 'bg-outdoor', label: 'Outdoor', checked: false },
    { id: 'bg-indoor', label: 'Indoor', checked: false },
    { id: 'bg-abstract', label: 'Abstract', checked: false },
    { id: 'bg-minimal', label: 'Minimal', checked: false }
  ] as FilterOption[],
  
  environmentOptions: [
    { id: 'env-forest', label: 'Forest', checked: false },
    { id: 'env-city', label: 'City', checked: false },
    { id: 'env-beach', label: 'Beach', checked: false },
    { id: 'env-mountains', label: 'Mountains', checked: false }
  ] as FilterOption[],
  
  moodOptions: [
    { id: 'mood-calm', label: 'Calm', checked: false },
    { id: 'mood-energetic', label: 'Energetic', checked: false },
    { id: 'mood-dramatic', label: 'Dramatic', checked: false },
    { id: 'mood-peaceful', label: 'Peaceful', checked: false }
  ] as FilterOption[]
};

// Centralized state interface
interface VideoFiltersState {
  hasAudio: boolean;
  selectedStyle: string | null;
  selectedInfluence: string | null;
  selectedBackground: string | null;
  videoDescription: string;
  isLoading: boolean;
}

// Action types for atomic updates
type VideoFiltersAction =
  | { type: 'SET_AUDIO'; payload: boolean }
  | { type: 'SET_STYLE'; payload: string | null }
  | { type: 'SET_INFLUENCE'; payload: string | null }
  | { type: 'SET_BACKGROUND'; payload: string | null }
  | { type: 'SET_DESCRIPTION'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESET_FILTERS' };

// Reducer for atomic state updates
const videoFiltersReducer = (state: VideoFiltersState, action: VideoFiltersAction): VideoFiltersState => {
  switch (action.type) {
    case 'SET_AUDIO':
      return { ...state, hasAudio: action.payload };
    case 'SET_STYLE':
      return { ...state, selectedStyle: action.payload };
    case 'SET_INFLUENCE':
      return { ...state, selectedInfluence: action.payload };
    case 'SET_BACKGROUND':
      return { ...state, selectedBackground: action.payload };
    case 'SET_DESCRIPTION':
      return { ...state, videoDescription: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'RESET_FILTERS':
      return {
        ...state,
        selectedStyle: null,
        selectedInfluence: null,
        selectedBackground: null,
        videoDescription: ''
      };
    default:
      return state;
  }
};

// Initial state
const initialState: VideoFiltersState = {
  hasAudio: true,
  selectedStyle: null,
  selectedInfluence: null,
  selectedBackground: null,
  videoDescription: '',
  isLoading: false
};

// Simplified interface
export interface UseVideoFiltersReturn {
  // State
  hasAudio: boolean;
  selectedStyle: string | null;
  selectedInfluence: string | null;
  selectedBackground: string | null;
  videoDescription: string;
  debouncedVideoDescription: string;
  isLoading: boolean;
  
  // Static options
  styleOptions: FilterOption[];
  influenceOptions: FilterOption[];
  colorOptions: FilterOption[];
  backgroundOptions: FilterOption[];
  environmentOptions: FilterOption[];
  moodOptions: FilterOption[];
  
  // Actions
  setHasAudio: (hasAudio: boolean) => void;
  setSelectedStyle: (style: string | null) => void;
  setSelectedInfluence: (influence: string | null) => void;
  setSelectedBackground: (background: string | null) => void;
  setVideoDescription: (description: string) => void;
  
  // Handlers for FilterDropdown components
  handleStyleChange: (options: FilterOption[]) => void;
  handleInfluenceChange: (options: FilterOption[]) => void;
  handleColorChange: (options: FilterOption[]) => void;
  handleBackgroundChange: (options: FilterOption[]) => void;
  handleEnvironmentChange: (options: FilterOption[]) => void;
  handleMoodChange: (options: FilterOption[]) => void;
}

/**
 * Optimized video filters hook with atomic state updates
 */
export const useVideoFilters = (): UseVideoFiltersReturn => {
  // Single state source with atomic updates
  const [state, dispatch] = useReducer(videoFiltersReducer, initialState);
  
  // Debounced description (only one debounce operation)
  const debouncedVideoDescription = useDebouncedValue(state.videoDescription, 500);
  
  // Stable action creators
  const setHasAudio = useCallback((hasAudio: boolean) => {
    dispatch({ type: 'SET_AUDIO', payload: hasAudio });
  }, []);
  
  const setSelectedStyle = useCallback((style: string | null) => {
    dispatch({ type: 'SET_STYLE', payload: style });
  }, []);
  
  const setSelectedInfluence = useCallback((influence: string | null) => {
    dispatch({ type: 'SET_INFLUENCE', payload: influence });
  }, []);
  
  const setSelectedBackground = useCallback((background: string | null) => {
    dispatch({ type: 'SET_BACKGROUND', payload: background });
  }, []);
  
  const setVideoDescription = useCallback((description: string) => {
    dispatch({ type: 'SET_DESCRIPTION', payload: description });
  }, []);
  
  // Stable handlers for FilterDropdown components
  const handleStyleChange = useCallback((options: FilterOption[]) => {
    const checkedOption = options.find(o => o.checked);
    setSelectedStyle(checkedOption ? checkedOption.id : null);
  }, [setSelectedStyle]);
  
  const handleInfluenceChange = useCallback((options: FilterOption[]) => {
    const checkedOption = options.find(o => o.checked);
    setSelectedInfluence(checkedOption ? checkedOption.id : null);
  }, [setSelectedInfluence]);
  
  const handleColorChange = useCallback((options: FilterOption[]) => {
    // Placeholder for multi-select color filters
    console.log('Color options changed:', options);
  }, []);
  
  const handleBackgroundChange = useCallback((options: FilterOption[]) => {
    const checkedOption = options.find(o => o.checked);
    setSelectedBackground(checkedOption ? checkedOption.id : null);
  }, [setSelectedBackground]);
  
  const handleEnvironmentChange = useCallback((options: FilterOption[]) => {
    // Placeholder for multi-select environment filters
    console.log('Environment options changed:', options);
  }, []);
  
  const handleMoodChange = useCallback((options: FilterOption[]) => {
    // Placeholder for multi-select mood filters
    console.log('Mood options changed:', options);
  }, []);
  
  return {
    // State
    hasAudio: state.hasAudio,
    selectedStyle: state.selectedStyle,
    selectedInfluence: state.selectedInfluence,
    selectedBackground: state.selectedBackground,
    videoDescription: state.videoDescription,
    debouncedVideoDescription,
    isLoading: state.isLoading,
    
    // Static options (no recreation)
    styleOptions: STATIC_FILTER_OPTIONS.styleOptions,
    influenceOptions: STATIC_FILTER_OPTIONS.influenceOptions,
    colorOptions: STATIC_FILTER_OPTIONS.colorOptions,
    backgroundOptions: STATIC_FILTER_OPTIONS.backgroundOptions,
    environmentOptions: STATIC_FILTER_OPTIONS.environmentOptions,
    moodOptions: STATIC_FILTER_OPTIONS.moodOptions,
    
    // Actions
    setHasAudio,
    setSelectedStyle,
    setSelectedInfluence,
    setSelectedBackground,
    setVideoDescription,
    
    // Handlers
    handleStyleChange,
    handleInfluenceChange,
    handleColorChange,
    handleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange
  };
}; 